/**
 * ChurchPublicPageEditor — admin tab for editing a single church's public page.
 *
 * Posts the full PublicPageIn schema to PUT /api/admin/churches/{id}/public-page.
 * Most fields are simple text/URL inputs; the gallery is a "URL + caption" list
 * (admins paste existing image URLs — same pattern the events module uses).
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MediaUpload } from '@/components/MediaUpload';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ExternalLink, Save, Plus, Trash2, ImagePlus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const adminFetch = async (path, opts = {}) => {
  const token = localStorage.getItem('eventflow_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
  const r = await fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${r.status}`);
  }
  return r.json();
};

export const ChurchPublicPageEditor = ({ churchId }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['church-public-page', churchId],
    queryFn: () => adminFetch(`/api/admin/churches/${churchId}/public-page`),
    enabled: !!churchId,
  });

  useEffect(() => {
    if (data?.page) setForm(JSON.parse(JSON.stringify(data.page)));
  }, [data]);

  const save = useMutation({
    mutationFn: () => adminFetch(`/api/admin/churches/${churchId}/public-page`, {
      method: 'PUT',
      body: JSON.stringify(form),
    }),
    onSuccess: (r) => {
      toast.success('Public page saved');
      qc.invalidateQueries({ queryKey: ['church-public-page', churchId] });
      setForm((f) => ({ ...f, slug: r.slug }));
    },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading || !form) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const publicUrl = form.slug ? `/church/${form.slug}` : '';
  // Convenience setters
  const setBlock = (key, partial) => setForm({ ...form, [key]: { ...(form[key] || {}), ...partial } });

  return (
    <div className="space-y-5" data-testid="church-public-page-editor">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <CardTitle>Public website</CardTitle>
              <CardDescription>
                Each church gets its own page at <span className="font-mono">/church/&lt;slug&gt;</span>. The bookshop &amp; newsletter
                sections route to the shared HQ store and subscriber list — only the per-church sections (hero, about, gallery, events, contact) live on this church.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="published" className="text-xs">Published</Label>
                <Switch
                  id="published"
                  checked={!!form.is_published}
                  onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                  data-testid="cp-published-toggle"
                />
              </div>
              {publicUrl && form.is_published && (
                <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-700 hover:underline inline-flex items-center" data-testid="cp-view-link">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> View page
                </a>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">URL slug</Label>
              <Input
                value={form.slug || ''}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-') })}
                placeholder="e.g. fwbc-harare"
                className="font-mono"
                data-testid="cp-slug-input"
              />
              <p className="text-[10px] text-slate-500 mt-1">{publicUrl ? `Live at ${publicUrl}` : 'Auto-derives from church name when blank'}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Primary colour</Label>
                <Input type="color" value={form.hero?.primary_color || '#0f766e'} onChange={(e) => setBlock('hero', { primary_color: e.target.value })} data-testid="cp-primary-color" />
              </div>
              <div>
                <Label className="text-xs">Accent colour</Label>
                <Input type="color" value={form.hero?.accent_color || '#f59e0b'} onChange={(e) => setBlock('hero', { accent_color: e.target.value })} data-testid="cp-accent-color" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============== HERO ============== */}
      <SectionCard title="Hero banner" id="hero">
        <MediaUpload value={form.hero?.banner_url || ''} purpose="banner"
                     label="Banner image (1600×900 works best)" testId="cp-hero-banner"
                     onChange={(url) => setBlock('hero', { banner_url: url })} />
        <Input placeholder="Tagline (small uppercase text)" value={form.hero?.tagline || ''} onChange={(e) => setBlock('hero', { tagline: e.target.value })} data-testid="cp-hero-tagline" />
        <Input placeholder="Main headline" value={form.hero?.headline || ''} onChange={(e) => setBlock('hero', { headline: e.target.value })} data-testid="cp-hero-headline" />
      </SectionCard>

      {/* ============== ABOUT ============== */}
      <SectionCard title="About / Pastor message">
        <Input placeholder="Section title" value={form.about?.title || ''} onChange={(e) => setBlock('about', { title: e.target.value })} />
        <Textarea placeholder="About body — describe this church, its mission, history…" rows={5} value={form.about?.body || ''} onChange={(e) => setBlock('about', { body: e.target.value })} data-testid="cp-about-body" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
          <Input placeholder="Pastor's name" value={form.about?.pastor_name || ''} onChange={(e) => setBlock('about', { pastor_name: e.target.value })} />
          <MediaUpload value={form.about?.pastor_photo_url || ''} purpose="avatar"
                       label="Pastor's photo" testId="cp-pastor-photo"
                       onChange={(url) => setBlock('about', { pastor_photo_url: url })} />
        </div>
        <Textarea placeholder="Pastor's short message (1-3 sentences)" rows={3} value={form.about?.pastor_message || ''} onChange={(e) => setBlock('about', { pastor_message: e.target.value })} />
      </SectionCard>

      {/* ============== EVENTS ============== */}
      <SectionCard
        title="Events feed"
        toggleable
        enabled={form.events?.enabled !== false}
        onToggle={(v) => setBlock('events', { enabled: v })}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Section title</Label>
            <Input value={form.events?.title || ''} onChange={(e) => setBlock('events', { title: e.target.value })} placeholder="Upcoming Events" />
          </div>
          <div>
            <Label className="text-xs">Show</Label>
            <Select
              value={form.events?.mode || 'all'}
              onValueChange={(v) => setBlock('events', { mode: v })}
            >
              <SelectTrigger data-testid="cp-events-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All upcoming events</SelectItem>
                <SelectItem value="this_church">Only events tagged to this church</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </SectionCard>

      {/* ============== BOOKSHOP / NEWSLETTER toggles (shared HQ content) ============== */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Shared HQ sections</CardTitle>
          <CardDescription>These point at the single global bookshop and newsletter list. You can hide either section on this church's page if you don't want them shown.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ToggleRow
            label="Bookshop tile"
            description={`Links visitors to the shared Bookshop (paid sermons, books, downloads).`}
            checked={form.bookshop?.enabled !== false}
            onChange={(v) => setBlock('bookshop', { enabled: v })}
            testid="cp-bookshop-toggle"
          >
            <Input placeholder="Section title" value={form.bookshop?.title || ''} onChange={(e) => setBlock('bookshop', { title: e.target.value })} />
            <Input placeholder="CTA button text" value={form.bookshop?.cta_text || ''} onChange={(e) => setBlock('bookshop', { cta_text: e.target.value })} />
          </ToggleRow>

          <ToggleRow
            label="Newsletter signup"
            description="Signups land in the global HQ list (no separate list per church)."
            checked={form.newsletter?.enabled !== false}
            onChange={(v) => setBlock('newsletter', { enabled: v })}
            testid="cp-newsletter-toggle"
          >
            <Input placeholder="Section title" value={form.newsletter?.title || ''} onChange={(e) => setBlock('newsletter', { title: e.target.value })} />
            <Textarea rows={2} placeholder="Short blurb above the email field" value={form.newsletter?.body || ''} onChange={(e) => setBlock('newsletter', { body: e.target.value })} />
          </ToggleRow>

          <ToggleRow
            label="Donate / Support"
            description={`Links visitors to a campaign on this site. Leave the slug blank to use the HQ default ('abmhc').`}
            checked={form.donate?.enabled !== false}
            onChange={(v) => setBlock('donate', { enabled: v })}
            testid="cp-donate-toggle"
          >
            <Input placeholder="Campaign slug (e.g. abmhc)" value={form.donate?.campaign_slug || ''} onChange={(e) => setBlock('donate', { campaign_slug: e.target.value })} />
          </ToggleRow>
        </CardContent>
      </Card>

      {/* ============== GALLERY ============== */}
      <SectionCard
        title="Photo gallery"
        toggleable
        enabled={form.gallery?.enabled !== false}
        onToggle={(v) => setBlock('gallery', { enabled: v })}
      >
        <Input placeholder="Section title" value={form.gallery?.title || ''} onChange={(e) => setBlock('gallery', { title: e.target.value })} />
        <GalleryList
          items={form.gallery?.items || []}
          onChange={(items) => setBlock('gallery', { items })}
        />
      </SectionCard>

      {/* ============== CONTACT ============== */}
      <SectionCard title="Contact">
        <Input placeholder="Address" value={form.contact?.address || ''} onChange={(e) => setBlock('contact', { address: e.target.value })} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="Phone" value={form.contact?.phone || ''} onChange={(e) => setBlock('contact', { phone: e.target.value })} />
          <Input placeholder="Email" value={form.contact?.email || ''} onChange={(e) => setBlock('contact', { email: e.target.value })} />
          <Input placeholder="WhatsApp (e.g. +263…)" value={form.contact?.whatsapp || ''} onChange={(e) => setBlock('contact', { whatsapp: e.target.value })} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="Facebook URL" value={form.contact?.facebook || ''} onChange={(e) => setBlock('contact', { facebook: e.target.value })} />
          <Input placeholder="YouTube URL" value={form.contact?.youtube || ''} onChange={(e) => setBlock('contact', { youtube: e.target.value })} />
          <Input placeholder="Instagram URL" value={form.contact?.instagram || ''} onChange={(e) => setBlock('contact', { instagram: e.target.value })} />
        </div>
        <Input placeholder="Google Maps embed URL (optional)" value={form.contact?.map_embed_url || ''} onChange={(e) => setBlock('contact', { map_embed_url: e.target.value })} />
      </SectionCard>

      <div className="sticky bottom-4 bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-lg p-3 flex items-center justify-end gap-2" data-testid="cp-save-bar">
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="cp-save-btn">
          {save.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          Save public page
        </Button>
      </div>
    </div>
  );
};

/* ─────────────── Small reusables ─────────────── */

const SectionCard = ({ title, children, toggleable, enabled, onToggle }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="text-base">{title}</CardTitle>
        {toggleable && (
          <div className="flex items-center gap-2">
            <Label className="text-xs">Enabled</Label>
            <Switch checked={!!enabled} onCheckedChange={onToggle} />
          </div>
        )}
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      {children}
    </CardContent>
  </Card>
);

const ToggleRow = ({ label, description, checked, onChange, children, testid }) => (
  <div className="rounded-lg border p-3 bg-slate-50">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {description && <p className="text-[11px] text-slate-500 mt-0.5">{description}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
    {checked && <div className="mt-3 space-y-2">{children}</div>}
  </div>
);

const GalleryList = ({ items, onChange }) => {
  const add = () => onChange([...(items || []), { url: '', caption: '' }]);
  const update = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => {
    const next = [...items];
    next.splice(i, 1);
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {(items || []).map((g, i) => (
        <div key={i} className="flex items-start gap-2" data-testid={`cp-gallery-item-${i}`}>
          <MediaUpload value={g.url || ''} onChange={(url) => update(i, { url })} purpose="gallery" testId={`cp-gallery-upload-${i}`} className="flex-1" previewClassName="h-16" />
          <Input placeholder="Caption (optional)" value={g.caption || ''} onChange={(e) => update(i, { caption: e.target.value })} className="flex-1" />
          <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(i)} aria-label="Remove">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} data-testid="cp-gallery-add">
        <ImagePlus className="h-4 w-4 mr-1" /> Add photo
      </Button>
      {(items || []).length === 0 && <p className="text-[11px] text-slate-400 italic">Add photos straight from your phone or computer — they are stored with the church.</p>}
    </div>
  );
};

export default ChurchPublicPageEditor;
